import {
	Alert,
	Bullseye,
	Button,
	Checkbox,
	Flex,
	FlexItem,
	FormGroup,
	HelperText,
	HelperTextItem,
	TreeView,
} from "@patternfly/react-core";
import { Modal, ModalVariant } from "@patternfly/react-core/deprecated";
import { ErrorPopup, SuccessPopup, useAlertContext } from "@rhc-shared-components/alert-component";
import { FormContainer } from "@rhc-shared-components/form";
import { FormTextInput } from "@rhc-shared-components/form-text-input";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Formik } from "formik";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { createProductVersion, patchProductListing, updateProductVersions } from "../../../api/pyxis";
import type { IErrorType } from "../../../constants/pfsContants";
import {
	CreateProductVersionModalKeys,
	EPartnerValidationProgress,
	OpenShiftFullName,
	ProductKeys,
	RHELFullName,
} from "../../../constants/product";
import { useCheckedItems } from "../../../hooks/useCheckedItems";
import { useProductMetadata } from "../../../hooks/useProductMetadata";
import { useTreeData } from "../../../hooks/useTreeData";
import type {
	IProductVersionsEntity,
	ProductVersionRedhatAffinityEntity,
	RedhatAffinityEntity,
} from "../../../interfaces/graphql";
import type { IPyxisAPIError } from "../../../interfaces/pyxis";
import {
	invalidateProductQueries,
	productDetailQueryKey,
	useRedhatProductVersions,
} from "../../../queries/productDetails";
import type { ProductListing } from "../../../types/generated-graphql";
import Spinner from "../../general/Spinner/Spinner";
import { ProductVersionModalValidator } from "./ProductVersionModalValidator";
import "./ProductVersionModal.scss";

interface ProductVersionModalProps {
	handleProductVersionModal: (isVisible: boolean) => void;
	modalState: { isVisible: boolean; productVersionId?: string };
	productVersionData?: IProductVersionsEntity; // Product version data for edit mode
}

export const ProductVersionModal: FC<ProductVersionModalProps> = ({
	modalState,
	handleProductVersionModal,
	productVersionData,
}) => {
	const isEdit = !!modalState?.productVersionId;
	const createNewProductVersionsMutation = useMutation({
		mutationFn: async (request: Partial<IProductVersionsEntity>) => {
			return await createProductVersion(request);
		},
	});
	const patchProductVersionsMutation = useMutation({
		mutationFn: (request: Partial<IProductVersionsEntity>) => updateProductVersions(request),
	});
	const updateProductListingMutation = useMutation({
		mutationFn: (request: Partial<ProductListing>) => patchProductListing(request),
	});
	const { productDetail, productVersionsData, setProductVersionsState, partnerValidationProgress } =
		useProductMetadata();
	interface ProductVersionFormValues {
		[CreateProductVersionModalKeys.productVersion]: string;
		[CreateProductVersionModalKeys.selectedVersions]: RedhatAffinityEntity[];
	}
	const [initialValue, setInitialValue] = useState<ProductVersionFormValues>({
		[CreateProductVersionModalKeys.productVersion]: "",
		[CreateProductVersionModalKeys.selectedVersions]: [],
	});
	const [isOpenshiftChecked, setOpenShiftCheckBox] = useState(false);
	const [isRhelChecked, setRHELCheckBox] = useState(false);
	const [unsupportedVersionsError, setUnsupportedVersionsError] = useState<string>("");
	const queryClient = useQueryClient();
	const { data: rhProductVersions, isFetching: rhProductVersionsLoading, error } = useRedhatProductVersions();
	const previouslySelectedVersions = useMemo((): RedhatAffinityEntity[] => {
		if (!productVersionData?.redhat_affinity?.length || !rhProductVersions?.length) return [];
		return productVersionData.redhat_affinity.flatMap((affinityProduct) => {
			const isRHEL = affinityProduct.product_name === RHELFullName;
			const shortName = isRHEL ? "RHEL" : "RHOCP";
			const matchingProduct = rhProductVersions.find((pv) => pv?.redHatProduct?.shortName === shortName);
			if (!matchingProduct?.redHatProduct?.id) return [];
			const transformedVersions = (affinityProduct.versions || [])
				.map((versionName: string) => {
					const versionMatch = rhProductVersions.find(
						(pv) => pv?.name === versionName.trim() && pv?.redHatProduct?.shortName === shortName,
					);
					return { id: versionMatch?.id, name: versionName.trim() };
				})
				.filter((v): v is { id: number; name: string } => v.id !== undefined);
			return [
				{
					id: matchingProduct.redHatProduct.id,
					name: affinityProduct.product_name,
					short_name: shortName,
					support_future_versions: false,
					versions: transformedVersions,
				},
			];
		});
	}, [productVersionData, rhProductVersions]);
	const { openshiftTreeData, rhelTreeData, openShiftFutureVersionsMap } = useTreeData(
		rhProductVersions || [],
		previouslySelectedVersions,
	);
	const { setAlertState } = useAlertContext();
	const openShiftFutureVersions = useMemo(() => {
		return Object.values(openShiftFutureVersionsMap).flatMap((versions) =>
			versions.map((v) => ({ id: v.id, name: v.name })),
		);
	}, [openShiftFutureVersionsMap]);
	const { checkedItems, setCheckedItems, onCheck, mapTree } = useCheckedItems(
		openshiftTreeData,
		rhelTreeData,
		openShiftFutureVersions,
	);
	const futureVersionIds = useMemo(() => {
		return new Set(openShiftFutureVersions.map((v) => v.id));
	}, [openShiftFutureVersions]);

	const hasNonFutureVersion = useMemo(() => {
		return checkedItems.some((item) => item?.versions?.some((v) => !futureVersionIds.has(v.id)));
	}, [checkedItems, futureVersionIds]);
	const resetModalState = (formikProps?: { handleReset: () => void }) => {
		setInitialValue({
			[CreateProductVersionModalKeys.productVersion]: "",
			[CreateProductVersionModalKeys.selectedVersions]: [],
		});
		setCheckedItems([]);
		setOpenShiftCheckBox(false);
		setRHELCheckBox(false);
		setUnsupportedVersionsError("");
		if (formikProps) {
			formikProps.handleReset();
		}
	};

	const memoizedOpenshiftTreeData = useMemo(
		() => openshiftTreeData?.treeData?.map((item) => mapTree(item)),
		[openshiftTreeData, mapTree],
	);
	const memoizedRhelTreeData = useMemo(
		() => rhelTreeData?.treeData?.map((item) => mapTree(item)),
		[rhelTreeData, mapTree],
	);
	const transformRedhatAffinityData = useCallback(
		(redhatAffinity: ProductVersionRedhatAffinityEntity[]) => {
			if (!redhatAffinity?.length || !rhProductVersions?.length) {
				return [];
			}

			return redhatAffinity.map((affinityProduct) => {
				const isRHEL = affinityProduct.product_name === RHELFullName;
				const shortName = isRHEL ? "RHEL" : "RHOCP";

				// Find matching product from rhProductVersions to get product ID
				const matchingProduct = rhProductVersions?.find(
					(productVersion) => productVersion?.redHatProduct?.shortName === shortName,
				);

				// Transform version names (strings) to version objects with id and name
				const transformedVersions =
					affinityProduct.versions
						?.map((versionName: string) => {
							// Find the version in rhProductVersions to get its ID
							const versionMatch = rhProductVersions?.find(
								(productVersion) =>
									productVersion?.name === versionName.trim() && productVersion?.redHatProduct?.shortName === shortName,
							);
							return {
								id: versionMatch?.id,
								name: versionName.trim(),
							};
						})
						.filter((transformedVersion) => transformedVersion.id !== undefined) || [];

				return {
					id: matchingProduct?.redHatProduct?.id,
					name: affinityProduct.product_name,
					short_name: shortName,
					...(shortName !== "RHOCP" && { support_future_versions: affinityProduct.support_future_versions }),
					versions: transformedVersions,
				};
			});
		},
		[rhProductVersions],
	);

	// Initialize checked items and checkboxes on data load
	useEffect(() => {
		if (isEdit && productVersionData) {
			// Transform and set checked items only when in edit mode and data is provided
			const savedCheckedItems = transformRedhatAffinityData(productVersionData?.redhat_affinity || []);
			setInitialValue({
				[CreateProductVersionModalKeys.productVersion]: productVersionData?.product_version,
				[CreateProductVersionModalKeys.selectedVersions]: savedCheckedItems as RedhatAffinityEntity[],
			});
			setCheckedItems(savedCheckedItems as RedhatAffinityEntity[]);

			// Set checkboxes based on loaded data
			setOpenShiftCheckBox(
				productVersionData?.redhat_affinity?.some(
					(product) => product?.product_name === OpenShiftFullName && (product?.versions?.length ?? 0) > 0,
				) ?? false,
			);
			setRHELCheckBox(
				productVersionData?.redhat_affinity?.some(
					(product) =>
						product?.product_name === RHELFullName &&
						((product?.versions?.length ?? 0) > 0 || product?.support_future_versions),
				) ?? false,
			);
		} else {
			// Reset state when opening in add mode
			setInitialValue({
				[CreateProductVersionModalKeys.productVersion]: "",
				[CreateProductVersionModalKeys.selectedVersions]: [],
			});
			setCheckedItems([]);
			setOpenShiftCheckBox(false);
			setRHELCheckBox(false);
		}
	}, [isEdit, productVersionData, setCheckedItems, transformRedhatAffinityData]);

	useEffect(() => {
		if (modalState?.isVisible) {
			document.body.classList.add("stop-scrolling");
		} else {
			document.body.classList.remove("stop-scrolling");
		}
	}, [modalState?.isVisible]);

	useEffect(() => {
		if (!modalState?.isVisible || !rhProductVersions || rhProductVersions.length === 0) {
			setUnsupportedVersionsError("");
			return;
		}
		const allSelectedVersionIds = new Set<number>();
		checkedItems.forEach((affinity) => {
			affinity?.versions?.forEach((version) => {
				allSelectedVersionIds.add(version.id);
			});
		});
		const unsupportedSelectedVersions: string[] = [];
		rhProductVersions.forEach((elm) => {
			if (allSelectedVersionIds.has(elm?.id) && !elm?.isSupport && !futureVersionIds.has(elm?.id)) {
				unsupportedSelectedVersions.push(elm?.name);
			}
		});
		if (unsupportedSelectedVersions.length > 0) {
			setUnsupportedVersionsError(
				`Cannot submit with unsupported versions selected: ${unsupportedSelectedVersions.join(", ")}.`,
			);
		} else {
			setUnsupportedVersionsError("");
		}
	}, [modalState?.isVisible, rhProductVersions, checkedItems, futureVersionIds]);

	const createPartnerProductVersion = (
		request: Partial<IProductVersionsEntity>,
		setSubmitting: (isSubmitting: boolean) => void,
	) => {
		if (isEdit) {
			patchProductVersionsMutation.mutate(request, {
				onSuccess: async (response) => {
					const newProductVersionsList = productVersionsData?.filter((item) => item?._id !== response?._id);
					setProductVersionsState([response, ...newProductVersionsList]);
					SuccessPopup(setAlertState, "Product version details updated successfully");
					setSubmitting(false);
					document.body.classList.remove("stop-scrolling");
					handleProductVersionModal(false);
					queryClient.invalidateQueries({ queryKey: productDetailQueryKey(productDetail?._id || "") });
					invalidateProductQueries(queryClient, productDetail?._id || "");
				},
				onError: ({ detailMessage }: IErrorType) => {
					setSubmitting(false);
					ErrorPopup(setAlertState, detailMessage);
				},
			});
		} else {
			createNewProductVersionsMutation.mutate(request, {
				onSuccess: async (response) => {
					setProductVersionsState([response, ...productVersionsData]);
					SuccessPopup(setAlertState, "Product version saved successfully");
					if (partnerValidationProgress === EPartnerValidationProgress.None) {
						try {
							await updateProductListingMutation.mutateAsync({
								[ProductKeys.id]: productDetail._id,
								product_validation: { partner_validated: false },
							});
						} catch (error) {
							ErrorPopup(
								setAlertState,
								(error as IPyxisAPIError)?.detail || "Something went wrong while updating product validation",
							);
						}
					}
					await Promise.all([
						queryClient.invalidateQueries({ queryKey: productDetailQueryKey(productDetail?._id || "") }),
						invalidateProductQueries(queryClient, productDetail?._id || ""),
					]);
					setSubmitting(false);
					handleProductVersionModal(false);
				},
				onError: ({ detail }: IPyxisAPIError) => {
					setSubmitting(false);
					ErrorPopup(setAlertState, detail || "Something went wrong while saving product version details");
				},
			});
		}
	};

	return (
		<Formik
			key={modalState?.productVersionId || "new"} // Force Formik to remount when switching between add/edit modes
			initialValues={initialValue}
			enableReinitialize={true}
			validateOnMount={true}
			validateOnChange={true}
			validationSchema={ProductVersionModalValidator}
			onSubmit={(values: ProductVersionFormValues, { setSubmitting }) => {
				const selectedVersions = values?.[CreateProductVersionModalKeys.selectedVersions] || [];
				const request: Partial<IProductVersionsEntity> = {
					product_listing: productDetail._id,
					product_version: values.product_version,
					...(!!productVersionData && { _id: productVersionData._id }),

					redhat_affinity: [
						...(selectedVersions.length > 0
							? (selectedVersions
									?.filter((item: RedhatAffinityEntity) => item?.versions && item.versions.length > 0)
									?.map((elm: RedhatAffinityEntity) => ({
										product_name: elm?.name,
										...(elm?.short_name !== "RHOCP" && { support_future_versions: elm?.support_future_versions }),
										versions: elm?.versions?.map((version) => version?.name) ?? [],
									})) ?? [])
							: []),
					],
				};
				createPartnerProductVersion(request, setSubmitting);
			}}
		>
			{(formikProps) => {
				return (
					<Modal
						title="Product version"
						variant={ModalVariant.medium}
						id="newversion-modal"
						className="version-product-modal"
						isOpen={modalState.isVisible}
						onClose={() => {
							resetModalState(formikProps);
							handleProductVersionModal(false);
						}}
						footer={
							<Flex direction={{ default: "column" }} spaceItems={{ default: "spaceItemsNone" }}>
								{unsupportedVersionsError && (
									<FlexItem style={{ paddingBottom: "1em" }}>
										<HelperText>
											<HelperTextItem variant="error">{unsupportedVersionsError}</HelperTextItem>
											<HelperTextItem variant="error">Please uncheck these versions before submitting.</HelperTextItem>
										</HelperText>
									</FlexItem>
								)}
								<FlexItem>
									<Flex spaceItems={{ default: "spaceItemsSm" }}>
										<FlexItem>
											<Button
												variant="primary"
												key="Create partner product version"
												isLoading={
													formikProps.isSubmitting ||
													createNewProductVersionsMutation.isPending ||
													patchProductVersionsMutation.isPending
												}
												isDisabled={
													!!unsupportedVersionsError ||
													!hasNonFutureVersion ||
													!formikProps.isValid ||
													formikProps.isSubmitting ||
													!formikProps.dirty ||
													rhProductVersionsLoading ||
													createNewProductVersionsMutation.isPending ||
													patchProductVersionsMutation.isPending
												}
												data-analytics-category={`Product Info|${isEdit ? "Update version" : "Save version"}`}
												aria-label={`${isEdit ? "Update product version" : "Save version"}`}
												onClick={() => {
													formikProps.handleSubmit();
												}}
											>
												{`${isEdit ? "Update Version" : "Save version"}`}
											</Button>
										</FlexItem>
										<FlexItem>
											<Button
												key="cancel"
												variant="secondary"
												aria-label="Cancel product version and close modal"
												onClick={() => {
													resetModalState(formikProps);
													document.body.classList.remove("stop-scrolling");
													handleProductVersionModal(false);
												}}
											>
												Cancel
											</Button>
										</FlexItem>
									</Flex>
								</FlexItem>
							</Flex>
						}
					>
						{rhProductVersionsLoading ? (
							<div className={"product-version-modal__spinner-wrapper"}>
								<Spinner />
							</div>
						) : error ? (
							<div className={"product-version-modal__spinner-wrapper"}>
								<Bullseye>Something Went Wrong...</Bullseye>
							</div>
						) : (
							<FormContainer gridSpan={12}>
								<FormTextInput
									isRequired
									name={CreateProductVersionModalKeys.productVersion}
									label={"Partner Product version"}
								/>
								<FormGroup label={"What Red Hat platforms are compatible with this version?"} isRequired>
									<Checkbox
										id={"check-box-openshift"}
										label={"Red Hat OpenShift"}
										isChecked={isOpenshiftChecked}
										onChange={(_, isChecked) => {
											setOpenShiftCheckBox(isChecked);
											// Clear OpenShift data when unchecked
											if (!isChecked) {
												const currentValues = formikProps.values[CreateProductVersionModalKeys.selectedVersions] || [];
												const filteredValues = currentValues.filter(
													(item: RedhatAffinityEntity) => item?.short_name !== "RHOCP",
												);
												void formikProps.setFieldValue(CreateProductVersionModalKeys.selectedVersions, filteredValues);
												setCheckedItems(filteredValues);
											}
										}}
									/>
									{isOpenshiftChecked && (
										<>
											<TreeView
												aria-label="Select Red Hat OpenShift versions"
												data={memoizedOpenshiftTreeData}
												onCheck={(event, treeViewItem) => {
													onCheck(event, treeViewItem, formikProps);
												}}
												hasCheckboxes
												useMemo={true}
												variant="default"
											/>
											{openShiftFutureVersions.length > 0 &&
												formikProps.values?.[CreateProductVersionModalKeys.selectedVersions]?.some(
													(item: RedhatAffinityEntity) => {
														if (item?.short_name !== "RHOCP") return false;
														const futureIds = new Set(openShiftFutureVersions.map((v) => v.id));
														return item?.versions?.some((v) => futureIds.has(v.id));
													},
												) && (
													<div className="version-product-modal__future-versions-alert">
														<Alert variant="warning" isInline title="Product must follow OpenShift API guidelines">
															Selecting this option indicates you have read and understand the{" "}
															<a
																href="https://docs.openshift.com/container-platform/latest/rest_api/understanding-api-support-tiers.html"
																target="_blank"
																rel="noopener noreferrer"
															>
																OpenShift API Tier guidelines
															</a>
															.
															<br />
															<br />
															You assure this product only uses OpenShift APIs in tier 1 &amp; 2, and does not use APIs
															that have been deprecated.
														</Alert>
													</div>
												)}
										</>
									)}
									<Checkbox
										id={"check-box-rhel"}
										label={"Red Hat Enterprise Linux"}
										isChecked={isRhelChecked}
										onChange={(_, isChecked) => {
											setRHELCheckBox(isChecked);
											// Clear RHEL data when unchecked
											if (!isChecked) {
												const currentValues = formikProps.values[CreateProductVersionModalKeys.selectedVersions] || [];
												const filteredValues = currentValues.filter(
													(item: RedhatAffinityEntity) => item?.short_name !== "RHEL",
												);
												void formikProps.setFieldValue(CreateProductVersionModalKeys.selectedVersions, filteredValues);
												setCheckedItems(filteredValues);
											}
										}}
									/>
									{isRhelChecked && (
										<TreeView
											aria-label="Select Red Hat Enterprise Linux versions"
											data={memoizedRhelTreeData}
											onCheck={(event, treeViewItem) => {
												onCheck(event, treeViewItem, formikProps);
											}}
											hasCheckboxes
											useMemo={true}
											variant="default"
										/>
									)}
									{formikProps.touched?.[CreateProductVersionModalKeys.selectedVersions] &&
										formikProps.errors?.[CreateProductVersionModalKeys.selectedVersions] && (
											<div className="pf-v6-c-helper-text__item pf-m-error" id="version-error-message">
												<span className="pf-v6-c-helper-text__item-text">
													{formikProps.errors?.[CreateProductVersionModalKeys.selectedVersions] as string}
												</span>
											</div>
										)}
								</FormGroup>
							</FormContainer>
						)}
					</Modal>
				);
			}}
		</Formik>
	);
};
